// src/billing/BillingDashboard.js
import { API_BASE } from '../apiBase';
import AuditDrawer from '../lib/AuditDrawer';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const STATUS_TONE = {
  Paid: 'bg-green-600/80 text-white', Overdue: 'bg-red-600/80 text-white',
  Sent: 'bg-indigo-600/80 text-white', Viewed: 'bg-sky-600/80 text-white',
  Accepted: 'bg-green-600/80 text-white', Rejected: 'bg-red-600/80 text-white',
  Draft: 'bg-gray-600/80 text-white', Revised: 'bg-purple-600/80 text-white',
};
const INVOICE_STATUSES = ['Draft', 'Sent', 'Viewed', 'Paid', 'Overdue', 'Revised'];
const QUOTE_STATUSES = ['Draft', 'Sent', 'Viewed', 'Accepted', 'Rejected', 'Revised'];
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
const money = (n) => `$${Number(n || 0).toLocaleString()}`;

// Subtle search beside each card title — nearly invisible until used.
function CardSearch({ value, onChange }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="search…"
      className="bg-transparent border border-gray-800/0 hover:border-gray-800 focus:border-gray-700 rounded-lg px-2.5 py-1 text-sm text-zinc-300 placeholder-zinc-700 focus:outline-none w-28 focus:w-48 transition-all"
    />
  );
}

// Card shell: header (title, search to its right) + body showing ~3 rows then scrolling.
function TableCard({ title, search, setSearch, extra, children }) {
  return (
    <div className="bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden shadow-2xl">
      <div className="p-6 border-b border-gray-800 flex items-center gap-3">
        <h2 className="text-xl font-bold">{title}</h2>
        <CardSearch value={search} onChange={setSearch} />
        {extra}
      </div>
      <div className="overflow-auto max-h-[218px]">
        {children}
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase bg-gray-950 sticky top-0 z-10">{children}</th>;

function BillingDashboard() {
  const navigate = useNavigate();
  const role = localStorage.getItem('role') || 'employee';
  const isAdmin = ['admin', 'superadmin'].includes(role);
  const isSuperAdmin = role === 'superadmin';
  const authH = () => ({ headers: { Authorization: localStorage.getItem('token') } });

  // Right-click delete (super admin only)
  const [ctxMenu, setCtxMenu] = useState(null); // { kind: 'quote'|'invoice', id, label, x, y }
  const onRowContext = (kind, id, label) => (e) => {
    if (!isSuperAdmin) return;
    e.preventDefault();
    setCtxMenu({ kind, id, label, x: e.clientX, y: e.clientY });
  };
  // "Audit" from the right-click menu — one record's diary timeline.
  const [auditFor, setAuditFor] = useState(null); // { entityType, entityId, label }
  const openAudit = () => {
    const { kind, id, label } = ctxMenu;
    setCtxMenu(null);
    setAuditFor({ entityType: kind === 'quote' ? 'Quote' : kind === 'proposal' ? 'Proposal' : 'Invoice', entityId: id, label });
  };
  const deleteRecord = async () => {
    const { kind, id, label } = ctxMenu;
    setCtxMenu(null);
    if (!window.confirm(`Delete ${kind} ${label}? This can't be undone.`)) return;
    try {
      const ep = kind === 'quote' ? 'quotes' : kind === 'proposal' ? 'proposals' : 'invoices';
      await axios.delete(`${API_BASE}/${ep}/${id}`, authH());
      if (kind === 'quote') setQuotes(prev => prev.filter(q => q._id !== id));
      else if (kind === 'proposal') setProposals(prev => prev.filter(q => q._id !== id));
      else setInvoices(prev => prev.filter(i => i._id !== id));
    } catch (e) {
      window.alert('Delete failed: ' + (e.response?.data?.error || e.message));
    }
  };

  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [invSearch, setInvSearch] = useState('');
  const [qtSearch, setQtSearch] = useState('');
  const [projSearch, setProjSearch] = useState('');

  const [proposals, setProposals] = useState([]);
  useEffect(() => {
    axios.get(`${API_BASE}/invoices`, authH()).then(r => setInvoices(r.data || [])).catch(() => {});
    axios.get(`${API_BASE}/quotes`, authH()).then(r => setQuotes(r.data || [])).catch(() => {});
    axios.get(`${API_BASE}/proposals`, authH()).then(r => setProposals(r.data || [])).catch(() => {});
  }, []);

  /* ---- click text = copy; click cell whitespace = open the record ---- */
  const [copied, setCopied] = useState('');
  const copyText = (text, key) => (e) => {
    e.stopPropagation(); // don't trigger the row's open
    if (!text) return;
    try { navigator.clipboard && navigator.clipboard.writeText(String(text)); } catch (_) {}
    setCopied(key); setTimeout(() => setCopied(''), 1200);
  };
  const CopyText = ({ value, id, field, className }) => (
    <span className="relative inline-block">
      <span onClick={copyText(value, id + field)} className={className || ''}>{value}</span>
      {copied === id + field && (
        <span className="absolute -top-7 left-1/2 -translate-x-1/2 bg-gray-800 text-zinc-100 text-[10px] px-2 py-0.5 rounded shadow z-20 whitespace-nowrap">Copied!</span>
      )}
    </span>
  );

  /* ---- inline status pill (fixed-position menu, flips up near the bottom) ---- */
  const [statusMenu, setStatusMenu] = useState(null); // { kind, id, x, y }
  const openStatusMenu = (kind, id, opts) => (e) => {
    e.stopPropagation();
    if (statusMenu && statusMenu.id === id) { setStatusMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuH = opts.length * 30 + 12;
    const up = rect.bottom + menuH > window.innerHeight - 8;
    setStatusMenu({ kind, id, opts, x: rect.left, y: up ? rect.top - menuH - 4 : rect.bottom + 4 });
  };
  const pickStatus = (status) => {
    const { kind, id } = statusMenu;
    setStatusMenu(null);
    if (kind === 'invoice') {
      setInvoices(prev => prev.map(i => i._id === id ? { ...i, status } : i));
      axios.put(`${API_BASE}/invoices/${id}`, { status }, authH()).catch(e => console.error('Invoice status failed', e.message));
    } else if (kind === 'proposal') {
      setProposals(prev => prev.map(q => q._id === id ? { ...q, status } : q));
      axios.put(`${API_BASE}/proposals/${id}`, { status }, authH()).catch(e => console.error('Proposal status failed', e.message));
    } else {
      setQuotes(prev => prev.map(q => q._id === id ? { ...q, status } : q));
      axios.put(`${API_BASE}/quotes/${id}`, { status }, authH()).catch(e => console.error('Quote status failed', e.message));
    }
  };
  const StatusPill = ({ kind, id, value, opts }) => (
    <button onClick={openStatusMenu(kind, id, opts)} title="Click to change status"
      className={`inline-block px-3 py-1 text-xs font-medium rounded-full hover:ring-2 hover:ring-white/20 transition-shadow ${STATUS_TONE[value] || 'bg-gray-600/80 text-white'}`}>
      {value || '—'}
    </button>
  );

  // ---- quick stats (from real invoices) ----
  const stats = useMemo(() => {
    const open = invoices.filter(i => ['Sent', 'Viewed', 'Overdue'].includes(i.status));
    const now = new Date();
    const paidThisMonth = invoices
      .filter(i => i.status === 'Paid' && i.updatedAt && new Date(i.updatedAt).getMonth() === now.getMonth() && new Date(i.updatedAt).getFullYear() === now.getFullYear())
      .reduce((s, i) => s + (i.total || 0), 0);
    return {
      outstanding: open.reduce((s, i) => s + (i.total || 0), 0),
      paidThisMonth,
      overdue: invoices.filter(i => i.status === 'Overdue').length,
    };
  }, [invoices]);

  // ---- filtered lists ----
  const invRows = useMemo(() => {
    const q = invSearch.trim().toLowerCase();
    return invoices.filter(i => !q || [i.number, i.customer?.name, i.project?.title, i.project?.poNumber, i.status].some(v => String(v || '').toLowerCase().includes(q)));
  }, [invoices, invSearch]);

  // Revised = superseded paper (plumbing, not an outcome) — hidden by default
  // so every job shows only its LIVE quote. The toggle is the escape hatch.
  const [includeRevised, setIncludeRevised] = useState(false);
  const revisedCount = useMemo(() => quotes.filter(t => t.status === 'Revised').length, [quotes]);
  const qtRows = useMemo(() => {
    const q = qtSearch.trim().toLowerCase();
    const rows = [
      ...quotes.map(x => ({ ...x, _kind: 'quote' })),
      ...proposals.map(x => ({ ...x, _kind: 'proposal', total: x.totalLabor, customer: { name: x.preparedFor || x.relationship?.company || '—' } })),
    ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return rows
      .filter(t => includeRevised || t.status !== 'Revised')
      .filter(t => !q || [t.number, t.customer?.name, t.project?.title, t.project?.poNumber, t.projectName, t.status].some(v => String(v || '').toLowerCase().includes(q)));
  }, [quotes, proposals, qtSearch, includeRevised]);

  const projRows = useMemo(() => {
    const map = {};
    invoices.forEach(i => {
      if (!i.project) return;
      const key = i.project._id || i.project;
      const m = (map[key] = map[key] || { poNumber: i.project.poNumber || '—', project: i.project.title || '—', invoiced: 0, paid: 0 });
      m.invoiced += i.total || 0;
      if (i.status === 'Paid') m.paid += i.total || 0;
    });
    const q = projSearch.trim().toLowerCase();
    return Object.values(map)
      .map(m => ({ ...m, outstanding: m.invoiced - m.paid }))
      .filter(m => !q || [m.poNumber, m.project].some(v => String(v).toLowerCase().includes(q)))
      .sort((a, b) => b.outstanding - a.outstanding);
  }, [invoices, projSearch]);

  return (
    <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 space-y-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
        <h1 className="text-3xl font-bold tracking-tight">Billing Dashboard</h1>
        <div className="flex gap-4">
          {isAdmin && (
            <>
              <button
                onClick={() => navigate('/billing/create-quote')}
                className="px-6 py-3 bg-purple-700 hover:bg-purple-600 text-white rounded-xl font-medium transition-all"
              >
                New Quote
              </button>
              <button
                onClick={() => navigate('/billing/create-invoice')}
                className="px-6 py-3 bg-indigo-700 hover:bg-indigo-600 text-white rounded-xl font-medium transition-all"
              >
                New Invoice
              </button>
            </>
          )}
        </div>
      </div>

      {/* Quick Stats - Visible to everyone */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800/50 shadow-2xl">
          <p className="text-zinc-400 text-sm">Total Outstanding</p>
          <p className="text-3xl font-bold text-red-400 mt-2">{money(stats.outstanding)}</p>
        </div>
        <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800/50 shadow-2xl">
          <p className="text-zinc-400 text-sm">Paid This Month</p>
          <p className="text-3xl font-bold text-green-400 mt-2">{money(stats.paidThisMonth)}</p>
        </div>
        <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800/50 shadow-2xl">
          <p className="text-zinc-400 text-sm">Overdue Invoices</p>
          <p className="text-3xl font-bold text-yellow-400 mt-2">{stats.overdue}</p>
        </div>
      </div>

      {/* Recent Invoices */}
      <TableCard title="Recent Invoices" search={invSearch} setSearch={setInvSearch}>
        <table className="min-w-full divide-y divide-gray-800/60">
          <thead>
            <tr><Th>Number</Th><Th>Customer</Th><Th>Amount</Th><Th>Status</Th><Th>Date</Th></tr>
          </thead>
          <tbody className="divide-y divide-gray-800/40">
            {invRows.map((inv) => (
              <tr key={inv._id} onClick={() => isAdmin && navigate(`/billing/create-invoice?id=${inv._id}`)} onContextMenu={onRowContext('invoice', inv._id, inv.number)} className="hover:bg-gray-800/50 transition-colors cursor-pointer">
                <td className="px-6 py-4 whitespace-nowrap text-sm"><CopyText value={inv.number} id={inv._id} field="num" /></td>
                <td className="px-6 py-4 whitespace-nowrap text-sm"><CopyText value={inv.customer?.name || '—'} id={inv._id} field="cust" /></td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium"><CopyText value={money(inv.total)} id={inv._id} field="amt" /></td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusPill kind="invoice" id={inv._id} value={inv.status} opts={INVOICE_STATUSES} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm"><CopyText value={fmtDate(inv.invoiceDate || inv.createdAt)} id={inv._id} field="date" /></td>
              </tr>
            ))}
            {invRows.length === 0 && <tr><td colSpan={5} className="px-6 py-6 text-sm text-zinc-600">No invoices{invSearch ? ' match your search' : ' yet'}.</td></tr>}
          </tbody>
        </table>
      </TableCard>

      {/* Quotes — bid quotes (no PO yet) and project quotes together */}
      <TableCard title="Quotes & Proposals" search={qtSearch} setSearch={setQtSearch}
        extra={revisedCount > 0 && (
          <button onClick={() => setIncludeRevised(v => !v)}
            className="ml-auto text-[11px] text-zinc-600 hover:text-zinc-400 transition-colors">
            {includeRevised ? 'hide revised' : `include revised (${revisedCount})`}
          </button>
        )}>
        <table className="min-w-full divide-y divide-gray-800/60">
          <thead>
            <tr><Th>Number</Th><Th>Customer</Th><Th>For</Th><Th>Amount</Th><Th>Status</Th><Th>Date</Th></tr>
          </thead>
          <tbody className="divide-y divide-gray-800/40">
            {qtRows.map((qt) => (
              <tr key={qt._id} onClick={() => isAdmin && navigate(qt._kind === 'proposal' ? `/billing/create-proposal?id=${qt._id}` : `/billing/create-quote?id=${qt._id}`)} onContextMenu={onRowContext(qt._kind === 'proposal' ? 'proposal' : 'quote', qt._id, qt.number)} className="hover:bg-gray-800/50 transition-colors cursor-pointer">
                <td className="px-6 py-4 whitespace-nowrap text-sm"><span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded mr-2 ${qt._kind === 'proposal' ? 'bg-rose-500/15 text-rose-300' : 'bg-indigo-500/15 text-indigo-300'}`}>{qt._kind === 'proposal' ? 'PS' : 'QT'}</span><CopyText value={qt.number} id={qt._id} field="num" /></td>
                <td className="px-6 py-4 whitespace-nowrap text-sm"><CopyText value={qt.customer?.name || '—'} id={qt._id} field="cust" /></td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-400"><CopyText value={qt.project ? `PO ${qt.project.poNumber || ''} — ${qt.project.title || ''}` : (qt.bid ? (qt.bid.stage === 'Lost' ? 'Bid — LOST' : qt.bid.stage === 'Won' || qt.bid.stage === 'Scheduled' ? `Bid — ${qt.bid.stage}` : 'Bid (not won yet)') : (qt._kind === 'proposal' ? qt.projectName || '—' : '—'))} id={qt._id} field="for" /></td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium"><CopyText value={money(qt.total)} id={qt._id} field="amt" /></td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusPill kind={qt._kind === 'proposal' ? 'proposal' : 'quote'} id={qt._id} value={qt.status} opts={QUOTE_STATUSES} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm"><CopyText value={fmtDate(qt.createdAt)} id={qt._id} field="date" /></td>
              </tr>
            ))}
            {qtRows.length === 0 && <tr><td colSpan={6} className="px-6 py-6 text-sm text-zinc-600">No quotes{qtSearch ? ' match your search' : ' yet'}.</td></tr>}
          </tbody>
        </table>
      </TableCard>

      {/* Project Financial Summary - Only for admins/owner */}
      {isAdmin && (
        <TableCard title="Project Financial Summary" search={projSearch} setSearch={setProjSearch}>
          <table className="min-w-full divide-y divide-gray-800/60">
            <thead>
              <tr><Th>PO Number</Th><Th>Project</Th><Th>Invoiced</Th><Th>Paid</Th><Th>Outstanding</Th></tr>
            </thead>
            <tbody className="divide-y divide-gray-800/40">
              {projRows.map((proj, idx) => (
                <tr key={idx} className="hover:bg-gray-800/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium"><CopyText value={proj.poNumber} id={`p${idx}`} field="po" /></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm"><CopyText value={proj.project} id={`p${idx}`} field="title" /></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm"><CopyText value={money(proj.invoiced)} id={`p${idx}`} field="inv" /></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-400"><CopyText value={money(proj.paid)} id={`p${idx}`} field="paid" /></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-red-400"><CopyText value={money(proj.outstanding)} id={`p${idx}`} field="out" /></td>
                </tr>
              ))}
              {projRows.length === 0 && <tr><td colSpan={5} className="px-6 py-6 text-sm text-zinc-600">No invoiced projects{projSearch ? ' match your search' : ' yet'}.</td></tr>}
            </tbody>
          </table>
        </TableCard>
      )}

      {/* right-click menu — delete (super admin only) */}
      {ctxMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setCtxMenu(null)} onContextMenu={(e) => { e.preventDefault(); setCtxMenu(null); }} />
          <div className="fixed z-50 bg-gray-900 border border-gray-700 rounded-xl shadow-xl p-1 min-w-[10rem]"
            style={{ left: Math.min(ctxMenu.x, (window.innerWidth || 800) - 170), top: Math.min(ctxMenu.y, (window.innerHeight || 600) - 60) }}>
            <button onClick={openAudit} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-800 text-left text-sm text-zinc-300">
              Audit
            </button>
            <button onClick={deleteRecord} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-red-600/15 text-left text-sm text-red-400">
              Delete {ctxMenu.kind} {ctxMenu.label}
            </button>
          </div>
        </>
      )}

      {auditFor && (
        <AuditDrawer
          entityType={auditFor.entityType}
          entityId={auditFor.entityId}
          label={auditFor.label}
          onClose={() => setAuditFor(null)}
        />
      )}

      {/* status picker — fixed so table scroll/overflow can't clip it */}
      {statusMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setStatusMenu(null)} />
          <div className="fixed z-50 bg-gray-900 border border-gray-700 rounded-xl shadow-xl p-1 flex flex-col min-w-[8rem]"
            style={{ left: Math.min(statusMenu.x, (window.innerWidth || 800) - 140), top: Math.max(8, statusMenu.y) }}>
            {statusMenu.opts.map(s => (
              <button key={s} onClick={() => pickStatus(s)} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-gray-800 text-left">
                <span className={`w-2 h-2 rounded-full ${(STATUS_TONE[s] || 'bg-gray-500').split(' ')[0]}`} />
                <span className="text-[12px] text-zinc-300">{s}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default BillingDashboard;
